import { Add, Delete, Edit } from "@mui/icons-material"
import { Stack } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"
import type {
  BiomeshPlatformResponse,
  PlatformType,
} from "@phc/common/gen/threatAnalyticsGen"
import { useListPlatformsBiomeshPlatformListGet } from "@phc/common/gen/threatAnalyticsGen"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import Button from "../Shared/Button"
import { latLngToMgrs, nodeTypeLabels, parseCentroid } from "./copUtils"
import { DeleteNodeDialog } from "./DeleteNodeDialog"
import { NodeFormDialog } from "./NodeFormDialog"

export const NodeList: FC = () => {
  const { data: nodes, isLoading } = useListPlatformsBiomeshPlatformListGet({
    include: "operations",
  })

  const [formOpen, setFormOpen] = useState(false)
  const [editNode, setEditNode] = useState<
    BiomeshPlatformResponse | undefined
  >()
  const [deleteNode, setDeleteNode] = useState<
    BiomeshPlatformResponse | undefined
  >()

  const handleAdd = useCallback(() => {
    setEditNode(undefined)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((node: BiomeshPlatformResponse) => {
    setEditNode(node)
    setFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setFormOpen(false)
    setEditNode(undefined)
  }, [])

  const columns: GridColDef<BiomeshPlatformResponse>[] = useMemo(
    () => [
      { field: "name", headerName: "Name", flex: 1 },
      {
        field: "platform_type",
        headerName: "Type",
        flex: 0.8,
        valueFormatter: (value: PlatformType) => nodeTypeLabels[value],
      },
      {
        field: "centroid",
        headerName: "Location",
        flex: 1,
        valueGetter: (_value: string, row: BiomeshPlatformResponse) => {
          const coords = parseCentroid(row.centroid)
          return coords ? latLngToMgrs(coords.lat, coords.lng) : "—"
        },
      },
      {
        field: "operations",
        headerName: "Operations",
        flex: 1,
        valueGetter: (_value: unknown, row: BiomeshPlatformResponse) =>
          row.operations?.map(o => o.name).join(", ") || "—",
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.8,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : "—",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        disableExport: true,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setDeleteNode(row)}
          />,
        ],
      },
    ],
    [handleEdit]
  )

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Node
          </Button>
        </Stack>
        <BaseGrid
          rows={nodes ?? []}
          columns={columns}
          getRowId={row => row.platform_id}
          loading={isLoading}
          search
          showToolbar
          exported
          exportFileName="biomesh-nodes"
          placeholder="Search nodes..."
        />
      </Stack>
      <NodeFormDialog
        key={editNode?.platform_id ?? "new"}
        open={formOpen}
        onClose={handleCloseForm}
        node={editNode}
      />
      {deleteNode && (
        <DeleteNodeDialog
          open
          onClose={() => setDeleteNode(undefined)}
          node={deleteNode}
        />
      )}
    </>
  )
}
